import {
    GA4EcommerceGroup,
    GA4EcommerceParam,
    GA4EcommerceValues,
    GA4Item,
} from '../models/ecommerce.model';
import { Ga4LinkType } from '../models/event-data.model';
import { GA4EventType } from '../models/general.model';
import { YesOrNoTypeString } from '../models/user.model';
import * as Ga4DataUtils from '../utils/ga4-data.utils';

import { ApiAdjusment } from '@api/_api/api-order/api-order.models';
import { Adjustment, CartItem } from '@api/cart/cart.models';
import { DroppointListsDetail } from '@api/store-locator/droppoint-lists/models/droppoint-lists.models';
import { CurrencyInfo } from '@core/domain/models/store.models';
import { PageSection } from '@core/domain/types/page-sections.types';
import { CollectionModels } from '@features/cms-collection';
import {
    OrderConfirmation,
} from '@features/orders/order-confirmation/domain/order-confirmation.model';
import { DiscountPayload, PaymentPayload, SelectedDiscount } from '@features/payment';
import { ProductPublicModels } from '@features/products/product';
import { ShippingMethod } from '@features/shipping';
import { SidebarCustomComponents } from '@features/sidebar';
import { FeelPurchasedCoupon } from '@pages/checkout/summary/domain/models/summary.model';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { getFullPageTitle } from '@shared/analytics/GA4/helpers/ga4-context.helper';
import { CustomContext } from '@shared/analytics/GA4/models/configuration.model';
import { ElementData } from '@shared/directives/element-observer.directive';
import { MenuCategory } from '@shared/header/models/category.model';

/** ################## README ##################
 * Aquí se definirán SOLAMENTE las funciones principales para la generación del valor de cada propiedad,
 * las utilidades que se utilicen dentro de estas se incluirán en "ga4-data.utils". Por ejemplo, para la
 * propiedad "tax" la función principal será "getTaxValue" y estará aquí presente, la función
 * "getPriceToValue", que se usa internamente, se definirá en "ga4-data.utils.ts".
 */

/** Objeto base que se irá completando con los valores generados por los helpers */
export const baseEventProperties: GA4EcommerceGroup = {
    coupon: undefined,
    creative_name: undefined,
    creative_slot: undefined,
    currency: undefined,
    im_action_type: undefined,
    im_bundle_reference: undefined,
    im_cart_id: undefined,
    im_cart_number_items: undefined,
    im_category_view_type: undefined,
    im_discount_type: undefined,
    im_discount: undefined,
    im_drop_point: undefined,
    im_funnel_type: undefined,
    im_gift_card_type: undefined,
    im_gift_card_used: undefined,
    im_gift_card_value: undefined,
    im_items_number: undefined,
    im_link_text: undefined,
    im_link_type: undefined,
    im_payment_gift_ticket: undefined,
    im_payment_type: undefined,
    im_shipping_tier: undefined,
    item_list_id: undefined,
    item_list_name: undefined,
    items: undefined,
    location_id: undefined,
    promotion_id: undefined,
    promotion_name: undefined,
    shipping: undefined,
    tax: undefined,
    transaction_id: undefined,
    value: undefined,
};

/**
 * Lista de orígenes donde se solicita anexar el partnumber del producto objetivo al nombre de lista generado.
 * (Actualmente en desuso, nos transmiten que podrían requerirlo en el futuro)
 */
export const ListPlacesWithPartNumber: Array<EventFrom | SidebarCustomComponents> = [
    EventFrom.backSoon,
    EventFrom.cartYoda,
    EventFrom.comingSoon,
    EventFrom.completeLook,
    EventFrom.completeLookYoda,
    EventFrom.frecuentlyBoughtTogether,
    EventFrom.othersYouMayLike,
    EventFrom.productdetail,
    EventFrom.recentlyViewed,
    EventFrom.relateds,
    EventFrom.relatedsBuyed,
    EventFrom.similar,
    EventFrom.similaresYoda,
    EventFrom.topSales,
];

/**
 * Lista de lugares donde se solicita anexar el partnumber del producto objetivo al nombre de lista generado.
 * (Actualmente en desuso, nos transmiten que podrían requerirlo en el futuro)
 */
export const ListPagesWithPartNumber: PageSection[] = ['ItxProductPage', 'ItxCategoryPage'];

export const getNumberItems = (cartItems: CartItem[]): string => {
    return cartItems?.reduce((sum, item) => sum + item.quantity, 0).toString();
};

export const getCurrency = (currency: CurrencyInfo): string => {
    return currency?.currencyCode;
};

export const getPaymentType = (
    eventType: GA4EventType,
    paymentDetails: DiscountPayload | PaymentPayload,
    order: OrderConfirmation,
) => {
    const isOrder = Ga4DataUtils.isOrderEvent(eventType);

    if (isOrder) return order.payment.methods[0].type;
    return paymentDetails?.paymentMethodType;
};

export const getDiscountType = (
    eventType: GA4EventType,
    adjustments: Adjustment[],
    orderAdjustments: ApiAdjusment[],
): string => {
    const isOrder = Ga4DataUtils.isOrderEvent(eventType);
    const _adjustments = isOrder ? orderAdjustments : adjustments;
    return _adjustments?.map((adjusment) => adjusment.type).join(';') || undefined;
};

export const isGiftCardUsed = (eventType: GA4EventType, selectedDiscount: SelectedDiscount[], order: OrderConfirmation) => {
    let isGiftCardUsedCondition: boolean;
    if (Ga4DataUtils.isOrderEvent(eventType)) {
        isGiftCardUsedCondition = !!order.payment.methods.find((_payment) => _payment.name === 'GiftCard');
    } else {
        isGiftCardUsedCondition = selectedDiscount?.length > 0 && !!selectedDiscount.find((discount) => discount.discountDetails.values.paymentMethodType === 'GiftCard');
    }
    return isGiftCardUsedCondition ? YesOrNoTypeString.si : YesOrNoTypeString.no;
};

export const getGiftCardType = (
    eventType: GA4EventType,
    selectedDiscount: SelectedDiscount[],
    // order: Order
): string | undefined => {
    const isOrder = Ga4DataUtils.isOrderEvent(eventType);
    let giftCardTypes: string[];
    if (isOrder) {
        // NOTE: No recibimos el type de gifcard como en el checkout, analistas nos dicen que por ahora no es necesario
        // giftCardTypes = order.payment.find((payment) => payment.type === 'GiftCard')
        //     .map((payment) => payment.type);
        giftCardTypes = undefined;
    } else {
        giftCardTypes = selectedDiscount?.map((discount) => discount.discountDetails.values.giftCardType);
    }
    return giftCardTypes?.length ? giftCardTypes.join('|') : undefined;
};

export const getGiftCardValue = (
    eventType: GA4EventType,
    selectedDiscount: SelectedDiscount[],
    currencyInfo: CurrencyInfo,
    // order: Order,
) => {
    const isOrder = Ga4DataUtils.isOrderEvent(eventType);

    if (isOrder) return undefined; // NOTE: No recibimos el type de gifcard como en el checkout, analistas nos dicen que por ahora no es necesario

    if (!selectedDiscount?.length) {
        return Ga4DataUtils.getPriceToValue(0, currencyInfo);
    }

    const sum = selectedDiscount.reduce((acc, currentDiscount) => {
        return acc + Number.parseInt(currentDiscount.discountDetails.values.amount);
    }, 0);

    return Ga4DataUtils.getPriceToValue(sum, currencyInfo);
};

export const getCoupon = (coupon: FeelPurchasedCoupon[]): string => {
    return coupon?.[0]?.id || undefined;
};

/**
 * En caso de provenir de la funcionalidad de fast sint siempre será "pickup".
 * Si el evento está relacionado con una "order" en vez de un cart se obtendrá el método de envío de esta.
 * Si no coincide ninguna de las condiciones anteriores se obtendrá el método de envío del estado de shipping.
 */
export const getShippingType = (
    eventType: GA4EventType,
    eventFrom: EventFrom,
    shippingMethod: ShippingMethod,
    order: OrderConfirmation,
): string => {
    if (eventFrom === EventFrom.fastSint) return 'pickup';

    const isOrder = Ga4DataUtils.isOrderEvent(eventType);
    const _shippingMethod = isOrder ? order.shippingMethod : shippingMethod;

    return {
        Standard: 'delivery',
        Express: 'delivery_express',
        PickupInStore: 'pickup',
        Droppoint: 'droppoint',
        Packstation: 'packstation', // ?
        DeferredStandard: 'deferred_standard', // ?
    }[_shippingMethod?.dbcode] || _shippingMethod?.kind;
};

export const getTaxValue = (order: OrderConfirmation, currencyInfo: CurrencyInfo): string => {
    const isTaxArray = order?.taxArray?.length > 0;
    let taxValue: number;
    if (isTaxArray) {
        taxValue = order?.taxArray?.reduce((acc, curr) => {
            return acc + curr.price;
        }, 0);
    } else {
        taxValue = order.tax + order.shippingTax;
    }
    return Ga4DataUtils.getPriceToValue(taxValue, currencyInfo) || '0';
};

export const getLinkType = (product: ProductPublicModels.PublicProduct, $event: Event): Ga4LinkType => {
    if ($event) {
        return {
            SPAN: 'texto',
            U: 'texto',
            IMG: 'foto',
            DIV: ($event?.target as Element)?.parentElement?.getElementsByTagName('video').length ? 'video' : undefined,
        }[($event?.target as Element)?.tagName] as Ga4LinkType || 'foto';
    }
    if (product?.imageData) {
        if (product?.imageData?.type === 'image') {
            return 'foto';
        }
        return 'video';
    }
    return 'foto';
};

/**
 * Realiza lógica específica para las collection stories.
 * Obtiene el categoryId del elemento HTML proporcionado, luego extrae la información de la categoría que corresponde del array de cartegorías.
 * Si está disponible utiliza la información de sección y id de categoría para formar el promotion name, en caso contrario se utiliza el text content del elemento HTML.
 */
export const getPromotionName = (
    categories: MenuCategory[],
    elementData?: ElementData,
    collectionStory?: CollectionModels.CollectionStories,
): string | undefined => {
    if (collectionStory) {
        return Ga4DataUtils.getPromotionNameByCollectionStories(collectionStory);
    }

    const element = document.querySelector(`[data-reference="${elementData?.elementReference}"]`);
    const categoryDestination = Ga4DataUtils.getCategoryById(categories, elementData?.link?.categoryId);
    const promotionName = (element?.querySelector('a')?.querySelector('piece-text') || element?.querySelector('a'))?.textContent;
    const promotionNameFormatted = promotionName?.replace(/(\n)/g, ' ')?.trim();

    return categoryDestination ? `${categoryDestination.sectionPathEn.split('/').join('_')}_${categoryDestination.id}` : promotionNameFormatted;
};

// Actualmente no podemos obtener ningún tipo de ID
export const getPromotionId = (): string | undefined => {
    return undefined;
};

/**
 * Handles the process of creating the "locationId" property value as follows:
 * pos\_[position of the module on the page]\_mod\_[type of module]\_link\_[position of the element on a list] ("link" is not included on some cases)
 * */
export const getLocationId = (elementData: ElementData): string => {
    const {
        element, homeLayout, categoriesLists, isCategoriesList,
    } = Ga4DataUtils.getLocationData(elementData);
    let module = 0;
    let position = 0;

    if (homeLayout) {
        // Cálculo del órden del módulo en la home
        for (const childModule of Object.values(homeLayout.children)) {
            // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
            if (!!childModule.querySelector('render-link')) {
                ++module;
            }
            if (childModule.contains(element)) {
                // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
                if (!!childModule.querySelector('swiper')) {
                    // Cálculo de la posición de la card en el swiper
                    position = Ga4DataUtils.getSlideLocation(childModule, element);
                }
                break;
            }
        }
    }
    if (isCategoriesList) {
        // Location de una promotion en un listado de categorías
        return Ga4DataUtils.getPromoPositionCategoryList(element, isCategoriesList, homeLayout, categoriesLists, module);
    }
    // Location de una promotion en la layout de la home
    return Ga4DataUtils.getPromoPositionHomeLayout(isCategoriesList, position, module);
};

export const getCreativeSlot = () => {
    return undefined;
};

export const getCreativeName = () => {
    return undefined;
};

export const getImFunnelType = (eventFrom: EventFrom): string => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (eventFrom) {
        case EventFrom.fastSint:
            return 'fast_sint';
        case EventFrom.buyNow:
            return 'buy_now';
        default:
            return 'standard';
    }
};

/**
 * Genera los objetos de items de GA4 a partir de cart items o products.
 * Considera los items de order como cart items debido a la similitud de sus modelos de datos.
 */
export const getItems = (_ecommerceValues: GA4EcommerceValues): GA4Item[] => {
    const items = _ecommerceValues.purposeShopcart ? _ecommerceValues.purchaseOrderPreviewItems : _ecommerceValues.items;

    const isOrder = Ga4DataUtils.isOrderEvent(_ecommerceValues.eventType);

    if (isOrder) return Ga4DataUtils.getCartItems(_ecommerceValues.orderConfirmation.items, _ecommerceValues);

    if (items?.length) {
        const firstValue = items[0];
        if (Ga4DataUtils.isProduct(firstValue)) {
            return Ga4DataUtils.getItemsFromProducts(items as ProductPublicModels.PublicProduct[], _ecommerceValues);
        }
        if (Ga4DataUtils.isCartItem(firstValue)) {
            return Ga4DataUtils.getCartItems(items as CartItem[], _ecommerceValues);
        }
    }
    return [];
};

export const getImItemsNumber = (_ecommerceValues: GA4EcommerceValues): string => {
    if (_ecommerceValues.eventFrom === EventFrom.parrilla) {
        return (_ecommerceValues?.customCategoryProductsIdsLength
            || _ecommerceValues?.categoryProductsIdsLength)
            ?.toString();
    }
    return (_ecommerceValues.items?.length || 0).toString();
};

/**
 * Si se proporciona un id de droppoint se utilizará ese mismo.
 * Si el tipo de método de envío no es "pickup", "droppoint" o "dropbox" no tendrá ID de droppoint.
 * En caso de tener un tipo de método de envío que corresponde se obtiene su ID.
 */
export const getImDropPoint = (
    eventType: GA4EventType,
    droppointListsDetail: DroppointListsDetail,
    dropPointId: string,
    shippingMethod: ShippingMethod,
    order: OrderConfirmation,
): string => {
    if (dropPointId) return dropPointId;

    const isOrder = Ga4DataUtils.isOrderEvent(eventType);
    const shippingMethodKind = isOrder ? order?.shippingMethod?.kind : shippingMethod?.kind;
    const isDropPointShipping = ['pickup', 'droppoint', 'dropbox'].includes(shippingMethodKind);

    if (!isDropPointShipping) return 'none';

    if (isOrder) {
        return order?.shippingAddress?.physicalStoreId;
    }
    return Ga4DataUtils.getObjectId(droppointListsDetail);
};

/**
 * Si el eventType corresponde a un evento de order se genera el valor a partir del totalOrder del orderConfirmation.
 * Si el evento está relacionado a un solo item se genera el valor a partir de la información de este.
 * Si el evento incluye multiples items pero no tenemos la información calculcada de shopcart sumamos los precios de cada item.
 */
export const getValueParam = (
    values: GA4EcommerceValues,
): string => {
    const currency = values.customContext.context.store.details.currencyInfo;

    if (Ga4DataUtils.isOrderEvent(values.eventType)) {
        return Ga4DataUtils.getPriceToValue(
            values?.orderConfirmation?.totalOrder,
            currency,
        );
    }

    let itemsValue = values?.totalProducts;
    const isOnlyOneItem = values?.items?.length === 1;
    if (isOnlyOneItem) {
        const item = values?.items[0];
        if (Ga4DataUtils.isProduct(item) || (item as CartItem)?.quantity === 1) {
            itemsValue = Ga4DataUtils.getItemPrice(values.items[0]);
        } else if (Ga4DataUtils.isCartItem(item)) {
            itemsValue = Ga4DataUtils.getItemPrice(values.items[0]) * (item as CartItem).quantity;
        }
    }
    // Si añadimos más de un item al carrito (productos coordinados) y no se ha calculado el valor total
    if (!(isOnlyOneItem || itemsValue)) {
        for (const item of values.items) {
            itemsValue += Ga4DataUtils.getItemPrice(item);
        }
    }

    return Ga4DataUtils.getPriceToValue(
        itemsValue,
        currency,
    );
};

export const getCartId = (id: number | string): string | undefined => {
    if (!id) {
        return undefined;
    }
    return id !== '-1' ? id.toString() : undefined;
};

export const getTransactionId = (order: OrderConfirmation): string => {
    return order?.id?.toString();
};

export const getImPaymentGiftTicket = (
    eventType: GA4EventType,
    paymentTicket: boolean,
    order: OrderConfirmation,
): YesOrNoTypeString => {
    const isOrder = Ga4DataUtils.isOrderEvent(eventType);
    const hasPaymentGiftTicket = isOrder ? order.giftTicket : paymentTicket;

    return Ga4DataUtils.getBooleanSiNo(hasPaymentGiftTicket);
};

export const getImLinkPageTitle = (customContext: CustomContext, eventFrom: EventFrom) => {
    if (eventFrom === EventFrom.stories) {
        return getFullPageTitle(customContext, eventFrom);
    }

    return undefined;
};

/**
 * Función principal que mapea el parámetro con el helper que genera su valor para el objeto final de ecommerce.
 */
export const getParamValue = (param: GA4EcommerceParam, values: GA4EcommerceValues) => {
    // NOTE: Ordenado alfabéticamente
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (param) {
        case GA4EcommerceParam.coupon:
            return getCoupon(values.coupon);

        case GA4EcommerceParam.creative_name:
            return getCreativeName();

        case GA4EcommerceParam.creative_slot:
            return getCreativeSlot();

        case GA4EcommerceParam.currency:
            return getCurrency(values.customContext.context.store.details.currencyInfo);

        case GA4EcommerceParam.im_action_type:
            return values?.imActionType;

        case GA4EcommerceParam.im_bundle_reference:
            return Ga4DataUtils.getLastAnalyticsInfo(values.items?.[0] as CartItem)?.bundleReference
                || Ga4DataUtils.getImBundleReference(
                    values.eventType,
                    values.eventFrom,
                    values.imBundleReference,
                    values.productOriginData?.lastBundleReference,
                    values.items,
                );

        case GA4EcommerceParam.im_cart_id:
            return getCartId(values.im_cart_id);

        case GA4EcommerceParam.im_cart_number_items:
            return getNumberItems(values.im_cart_number_items);

        case GA4EcommerceParam.im_category_view_type:
            return `cat_${values.items.length}`;

        case GA4EcommerceParam.im_discount:
            return Ga4DataUtils.getDiscount(
                values.customContext.product,
                values.customContext.context.store.details.currencyInfo,
            );

        case GA4EcommerceParam.im_discount_type:
            return getDiscountType(
                values.eventType,
                values.im_discount_type,
                values?.orderConfirmation?.adjustments,
            );

        case GA4EcommerceParam.im_drop_point:
            return getImDropPoint(
                values?.eventType,
                values?.im_drop_point,
                values?.dropPointId,
                values.im_shipping_tier,
                values?.orderConfirmation,
            );

        case GA4EcommerceParam.im_funnel_type:
            return getImFunnelType(values.eventFrom);

        case GA4EcommerceParam.im_gift_card_type:
            return getGiftCardType(values.eventType, values.discount_data);

        case GA4EcommerceParam.im_gift_card_used:
            return isGiftCardUsed(values.eventType, values.discount_data, values.orderConfirmation);

        case GA4EcommerceParam.im_gift_card_value:
            return getGiftCardValue(
                values.eventType,
                values.discount_data,
                values.customContext.context.store.details.currencyInfo,
                // values.orderConfirmation,
            );

        case GA4EcommerceParam.im_items_number:
            return getImItemsNumber(values);

        case GA4EcommerceParam.im_link_page_title:
            return getImLinkPageTitle(values.customContext, values.eventFrom);

        case GA4EcommerceParam.im_link_text:
            return values.imLinkText.trim();

        case GA4EcommerceParam.im_link_type:
            return getLinkType(values?.items?.[0] as ProductPublicModels.PublicProduct, values?.elementData?.$event);

        case GA4EcommerceParam.im_payment_gift_ticket:
            return getImPaymentGiftTicket(
                values.eventType,
                values.im_payment_gift_ticket,
                values.orderConfirmation,
            );

        case GA4EcommerceParam.item_list_id:
            // No se utiliza a nivel de evento
            break;

        case GA4EcommerceParam.item_list_name:
            if (values.items?.length) {
                return Ga4DataUtils.getLastAnalyticsInfo(values.items[0] as CartItem)?.list || Ga4DataUtils.getItemListName(
                    values.eventFrom,
                    values.customContext.navigationData.page,
                    values.items?.[0],
                    values?.customContext.sidebarLoadedComponent?.name,
                    values?.customContext.categoryInfo.category,
                    values?.customContext.categoryInfo.categoriesNavigation,
                    values.baseProduct,
                    values.place,
                    values?.productOriginData,
                );
            }
            return undefined;

        case GA4EcommerceParam.items:
            return getItems(values).sort((a, b) => Number(a.index) - Number(b.index));

        case GA4EcommerceParam.location_id:
            return values?.sourcePromotion?.locationId || getLocationId(values?.elementData);

        case GA4EcommerceParam.im_payment_type:
        case GA4EcommerceParam.payment_type:
            return getPaymentType(values.eventType, values.im_payment_type, values.orderConfirmation);

        case GA4EcommerceParam.promotion_id:
            return getPromotionId();

        case GA4EcommerceParam.promotion_name:
            return values?.sourcePromotion?.promotionName
                || getPromotionName(values?.categories, values?.elementData, values?.collectionStory);

        case GA4EcommerceParam.shipping:
            return Ga4DataUtils.getPriceToValue(
                values?.orderConfirmation?.shippingPrice || values.shipping,
                values.customContext.context.store.details.currencyInfo,
            );

        case GA4EcommerceParam.im_shipping_tier:
        case GA4EcommerceParam.shipping_tier:
            return getShippingType(values.eventType, values.eventFrom, values.im_shipping_tier, values.orderConfirmation);

        case GA4EcommerceParam.tax:
            return getTaxValue(
                values?.orderConfirmation,
                values.customContext.context.store.details.currencyInfo,
            );

        case GA4EcommerceParam.transaction_id:
            return getTransactionId(values?.orderConfirmation);

        case GA4EcommerceParam.value:
            return getValueParam(values);

        default:
            return '';
    }
};

/**
 * Define los valores de las propiedades del objeto base incluidas en la configuración proporcionada
 * con los valores de retorno de la lógica que corresponde a cada parámetro.
 */
export const getParams = (config: GA4EcommerceParam[], values: GA4EcommerceValues): GA4EcommerceGroup => {
    const ecommerceObject: GA4EcommerceGroup = { ...baseEventProperties };
    for (const param of config) {
        ecommerceObject[param] = getParamValue(param, values);
    }

    return ecommerceObject;
};

export const ga4EcommerceProcessConfig = (
    config: GA4EcommerceParam[],
    _ecommerce: GA4EcommerceValues,
): GA4EcommerceGroup | undefined => {
    if (config?.length && _ecommerce) {
        return getParams(config, _ecommerce);
    }
    return undefined;
};
